import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../../types';
import { getTeamName } from '../../services/teamService';
import { getLeagueNameFromAny } from '../../services/leagueService';
import { t } from '../../i18n';

interface H2HMatch {
  fixture: {
    id: number;
    date: string;
  };
  league: {
    id?: number;
    name: string;
    logo: string;
  };
  teams: {
    home: { name: string; logo: string; winner: boolean | null; id: number | string };
    away: { name: string; logo: string; winner: boolean | null; id: number | string };
  };
  goals: {
    home: number;
    away: number;
  };
}

interface MatchH2HSectionProps {
  h2hData: H2HMatch[];
  lang: Language;
  homeTeamId: string;
  awayTeamId: string;
}

export const MatchH2HSection: React.FC<MatchH2HSectionProps> = ({
  h2hData,
  lang,
  homeTeamId,
  awayTeamId,
}) => {
  if (!h2hData || h2hData.length === 0) return null;

  const homeIdStr = homeTeamId.toString();
  const awayIdStr = awayTeamId.toString();
  
  // 통계 계산
  const stats = h2hData.reduce((acc, match) => {
    const homeIdMatch = match.teams.home.id.toString();
    const awayIdMatch = match.teams.away.id.toString();
    
    const isHomeTeamMatchHome = homeIdMatch === homeIdStr;
    const isHomeTeamMatchAway = awayIdMatch === homeIdStr;
    
    const isAwayTeamMatchHome = homeIdMatch === awayIdStr;
    const isAwayTeamMatchAway = awayIdMatch === awayIdStr;
    
    if (match.teams.home.winner === true) {
      if (isHomeTeamMatchHome) acc.homeWins++; 
      else if (isAwayTeamMatchHome) acc.awayWins++;
    } else if (match.teams.away.winner === true) {
      if (isHomeTeamMatchAway) acc.homeWins++; 
      else if (isAwayTeamMatchAway) acc.awayWins++;
    } else {
      acc.draws++;
    }
    return acc;
  }, { homeWins: 0, awayWins: 0, draws: 0 });

  const homeWinRate = Math.round((stats.homeWins / h2hData.length) * 100);
  const drawRate = Math.round((stats.draws / h2hData.length) * 100);
  const awayWinRate = 100 - homeWinRate - drawRate;

  const firstMatch = h2hData[0];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setIsAnimating(false);
    setTimeout(() => setIsModalOpen(false), 500);
  };

  const renderModal = () => {
    if (!isModalOpen) return null;

    return createPortal(
      <div className={`fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-0 md:p-6 transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
        <div 
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" 
          onClick={handleCloseModal}
        />
        <div className={`relative w-full max-w-lg bg-slate-50 rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-all duration-700 ease-[0.32,0.72,0,1] max-h-[85vh] md:max-h-[80vh] flex flex-col border-t md:border border-white/20 ${isAnimating ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-full md:translate-y-10 md:scale-95 opacity-0'}`}>
          {/* 드래그 핸들 느낌의 바 - 모바일에서만 표시 */}
          <div className="w-full flex justify-center pt-3 pb-1 bg-white shrink-0 md:hidden" onClick={handleCloseModal}>
            <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
          </div>
          
          <div className="p-6 pt-2 md:pt-6 flex items-center justify-between border-b border-slate-100 bg-white shrink-0">
            <div className="flex flex-col">
              <h3 className="text-md font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                {t(lang, 'headToHead')}
              </h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                 {t(lang, 'lastNMatches')?.replace('{n}', h2hData.length.toString()) || `최근 ${h2hData.length} 경기`}
              </span>
            </div>
            <button 
              onClick={handleCloseModal}
              className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 pb-12 custom-scrollbar">
            {h2hData.map((match) => {
              const date = new Date(match.fixture.date).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });

              return (
                <div key={match.fixture.id} className="flex flex-col bg-white border border-slate-100 p-4 rounded-2xl shadow-sm gap-3">
                  <div className="flex items-center justify-between items-start gap-1 shrink-0">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter truncate max-w-[100px]">
                      {getLeagueNameFromAny(match.league?.name, lang, match.league?.name)}
                    </span>
                    <span className="text-[10px] font-bold text-slate-500 tabular-nums">{date}</span>
                  </div>
                  
                  <div className="flex-1 flex items-center justify-between gap-2 ">
                    <div className="flex items-center gap-2 flex-1 justify-start min-w-0">
                      <span className={`text-[11px] font-bold truncate ${match.teams.home.winner ? 'text-rose-500' : 'text-slate-400'}`}>
                        {getTeamName(match.teams.home.id.toString(), lang, match.teams.home.name)}
                      </span>
                      <img src={match.teams.home.logo} alt="" className="w-6 h-6 object-contain shrink-0" />
                    </div>
                    
                    <div className="px-3 py-1 text-sm font-black tabular-nums shrink-0 flex items-center gap-1.5">
                      <span className={match.teams.home.winner ? 'text-rose-500 scale-110' : 'text-slate-900'}>{match.goals.home}</span>
                      <span className="text-slate-800 font-medium">:</span>
                      <span className={match.teams.away.winner ? 'text-rose-500 scale-110' : 'text-slate-900'}>{match.goals.away}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                      <img src={match.teams.away.logo} alt="" className="w-6 h-6 object-contain shrink-0" />
                      <span className={`text-[11px] font-bold truncate ${match.teams.away.winner ? 'text-rose-500' : 'text-slate-400'}`}>
                        {getTeamName(match.teams.away.id.toString(), lang, match.teams.away.name)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="mt-12 animate-in fade-in duration-700">
      <div className="flex items-center gap-2 mb-6 px-4 md:px-0">
        <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
          <span className="text-[10px] font-black uppercase">vs</span>
        </div>
        <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest">
          {t(lang, 'headToHead')}
        </h2>
      </div>

      <div className="mx-4 md:mx-0 bg-white rounded-[32px] p-6 shadow-sm shadow-slate-200/50 border border-slate-100 mb-8 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 via-slate-300 to-slate-900 opacity-20" />
        
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
              {t(lang, 'lastNMatches')?.replace('{n}', h2hData.length.toString()) || `최근 ${h2hData.length} 경기`}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col items-center gap-2 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center p-2 border border-slate-100 shadow-sm">
                <img src={`https://media.api-sports.io/football/teams/${homeTeamId}.png`} alt="" className="w-full h-full object-contain" />
              </div>
              <div className="text-center">
                <div className="text-xl font-black text-slate-900 leading-none">{stats.homeWins}</div>
                <div className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">{t(lang, 'wins')}</div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-1 px-4 border-x border-slate-100">
              <div className="text-sm font-black text-slate-300 uppercase tracking-tighter italic">VS</div>
              <div className="text-lg font-black text-slate-500 tabular-nums leading-none">{stats.draws}</div>
              <div className="text-[9px] font-bold text-slate-400 uppercase">{t(lang, 'draws')}</div>
            </div>

            <div className="flex flex-col items-center gap-2 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center p-2 border border-slate-100 shadow-sm">
                <img src={`https://media.api-sports.io/football/teams/${awayTeamId}.png`} alt="" className="w-full h-full object-contain" />
              </div>
              <div className="text-center">
                <div className="text-xl font-black text-slate-900 leading-none">{stats.awayWins}</div>
                <div className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">{t(lang, 'wins')}</div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex p-0.5 border border-slate-100 shadow-inner">
              <div 
                className="h-full bg-rose-500 transition-all duration-1000 rounded-l-full relative group" 
                style={{ width: `${homeWinRate}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
              </div>
              <div 
                className="h-full bg-slate-300 transition-all duration-1000 relative group" 
                style={{ width: `${drawRate}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
              </div>
              <div 
                className="h-full bg-slate-900 transition-all duration-1000 rounded-r-full relative group" 
                style={{ width: `${awayWinRate}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
              </div>
            </div>

            <div className="flex justify-between items-center px-1">
              <div className="flex flex-col items-start">
                <span className="text-[11px] font-black text-rose-500">{homeWinRate}%</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[10px] font-bold text-slate-400">{drawRate}%</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[11px] font-black text-slate-900">{awayWinRate}%</span>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full mt-4 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200 flex items-center justify-center gap-2"
        >
          {t(lang, 'viewH2HDetail') || '상대전적 상세 보기'}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {renderModal()}
    </div>
  );
};
